import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { KeyRound, Video, Users, Eye, Radio } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

interface Stats {
  totalVideos: number;
  publishedVideos: number;
  totalCdkCodes: number;
  usedCdkCodes: number;
  totalUsers: number;
}

export default function Dashboard() {
  const { toast } = useToast();
  const [stats, setStats] = useState<Stats>({
    totalVideos: 0,
    publishedVideos: 0,
    totalCdkCodes: 0,
    usedCdkCodes: 0,
    totalUsers: 0,
  });
  const [todayVisitors, setTodayVisitors] = useState<number | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<number | null>(null);
  const [userChart, setUserChart] = useState<{ date: string; visitors: number; signups: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [seriesOptions, setSeriesOptions] = useState<{ id: string; title: string }[]>([]);
  const [trialSeriesId, setTrialSeriesId] = useState<string | null>(null);
  const [savingTrial, setSavingTrial] = useState(false);

  useEffect(() => {
    async function fetchStats() {
      try {
        // Fetch video stats
        const { count: totalVideos } = await supabase
          .from("videos")
          .select("*", { count: "exact", head: true });

        const { count: publishedVideos } = await supabase
          .from("videos")
          .select("*", { count: "exact", head: true })
          .eq("is_published", true);

        // Fetch CDK stats
        const { count: totalCdkCodes } = await supabase
          .from("cdk_codes")
          .select("*", { count: "exact", head: true });

        const { count: usedCdkCodes } = await supabase
          .from("cdk_codes")
          .select("*", { count: "exact", head: true })
          .eq("is_used", true);

        // Fetch user stats
        const { count: totalUsers } = await supabase
          .from("profiles")
          .select("*", { count: "exact", head: true });

        setStats({
          totalVideos: totalVideos || 0,
          publishedVideos: publishedVideos || 0,
          totalCdkCodes: totalCdkCodes || 0,
          usedCdkCodes: usedCdkCodes || 0,
          totalUsers: totalUsers || 0,
        });

        const [
          { data: seriesData, error: seriesError },
          { data: settingsData, error: settingsError },
          { data: kpisData },
          { data: chartData },
        ] = await Promise.all([
          supabase.from("series").select("id,title").order("updated_at", { ascending: false }),
          supabase.from("app_settings").select("trial_series_id").eq("id", "global").maybeSingle(),
          supabase.rpc("admin_dashboard_kpis"),
          supabase.rpc("admin_dashboard_daily_users", { _days: 14 }),
        ]);

        if (seriesError) throw seriesError;
        if (settingsError) throw settingsError;

        setSeriesOptions((seriesData || []) as any);
        setTrialSeriesId((settingsData as any)?.trial_series_id || null);

        const kpis = (kpisData || [])[0] as any;
        setTodayVisitors(typeof kpis?.today_visitors === "number" ? kpis.today_visitors : null);
        setOnlineUsers(typeof kpis?.online_users === "number" ? kpis.online_users : null);

        setUserChart(
          ((chartData || []) as any[]).map((row) => ({
            date: String(row.date).slice(5),
            visitors: Number(row.visitors || 0),
            signups: Number(row.signups || 0),
          }))
        );
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, []);

  const statCards = [
    {
      title: "视频总数",
      value: stats.totalVideos,
      subValue: `${stats.publishedVideos} 已发布`,
      icon: Video,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      title: "卡密总数",
      value: stats.totalCdkCodes,
      subValue: `${stats.usedCdkCodes} 已使用`,
      icon: KeyRound,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
    },
    {
      title: "用户总数",
      value: stats.totalUsers,
      subValue: "注册用户",
      icon: Users,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
    },
    {
      title: "今日访问",
      value: todayVisitors ?? "-",
      subValue: "访问用户数",
      icon: Eye,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      title: "在线人数",
      value: onlineUsers ?? "-",
      subValue: "近 5 分钟活跃",
      icon: Radio,
      color: "text-sky-500",
      bgColor: "bg-sky-500/10",
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">控制台</h1>
        <p className="text-muted-foreground">欢迎回来，管理员</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.title}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold text-foreground mt-1">
                      {loading ? "-" : card.value}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {card.subValue}
                    </p>
                  </div>
                  <div className={cn(card.bgColor, "p-3 rounded-xl")}>
                    <Icon className={cn("h-6 w-6", card.color)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>用户趋势（近 14 天）</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <ChartContainer
            className="h-[260px] w-full"
            config={{
              visitors: { label: "访问用户", color: "hsl(var(--primary))" },
              signups: { label: "新注册", color: "hsl(var(--muted-foreground))" },
            }}
          >
            <LineChart data={userChart} margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={40} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Line
                type="monotone"
                dataKey="visitors"
                stroke="var(--color-visitors)"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="signups"
                stroke="var(--color-signups)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>快捷操作</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            使用左侧菜单管理卡密、视频和用户。
          </p>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>试看系列设置</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="w-full sm:max-w-md">
              <Select
                value={trialSeriesId || "none"}
                onValueChange={(v) => setTrialSeriesId(v === "none" ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择一个试看系列" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">未设置</SelectItem>
                  {seriesOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              disabled={savingTrial}
              onClick={async () => {
                setSavingTrial(true);
                try {
                  const { error } = await supabase
                    .from("app_settings")
                    .update({ trial_series_id: trialSeriesId })
                    .eq("id", "global");
                  if (error) throw error;
                  toast({ title: "已保存试看系列" });
                } catch (e: any) {
                  toast({
                    title: "保存失败",
                    description: e?.message || "请稍后重试",
                    variant: "destructive",
                  });
                } finally {
                  setSavingTrial(false);
                }
              }}
            >
              保存
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            普通用户仅能观看该系列的视频；会员用户不受限制。
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function cn(...classes: (string | undefined | false)[]) {
  return classes.filter(Boolean).join(" ");
}
